import React, { useEffect, useState } from "react";
import {
  Store,
  Plus,
  Pencil,
  Trash2,
  X,
  Tag,
  Percent,
  Share2,
  Film,
  FileDown,
  Sparkles,
  ImagePlus,
  Package,
  Crosshair,
} from "lucide-react";
import { toast } from "sonner";
import { ccCard, ccInput, ccLabel, ccBtnPink, ccBtnGhost } from "../care/ui";
import ImageCropModal from "../lib/ImageCrop";
import FocalPointModal from "../lib/FocalPoint";

const API = process.env.REACT_APP_BACKEND_URL;

export const mediaUrl = (p) => (p && !p.startsWith("http") ? `${API}/api/store/media/${p}` : p);

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const authed = async (path, options = {}) => {
  const res = await fetch(`${API}${path}`, { credentials: "include", ...options });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Request failed");
  return body;
};

const uploadMedia = async (file, attempt = 1) => {
  const fd = new FormData();
  fd.append("file", file);
  try {
    const res = await fetch(`${API}/api/store/admin/media`, { method: "POST", credentials: "include", body: fd });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Upload failed");
    return body;
  } catch (e) {
    // Network-level failures (TypeError: Failed to fetch) are usually transient — connection drops during deploys or big files on slow links. Retry up to 2 more times.
    if (e instanceof TypeError && attempt < 3) {
      toast.info(`Connection hiccup — retrying upload (${attempt}/2)…`);
      await new Promise((r) => setTimeout(r, 1500 * attempt));
      return uploadMedia(file, attempt + 1);
    }
    if (e instanceof TypeError) throw new Error("Upload failed — connection dropped. Check your internet and try again (large files need a stable connection).");
    throw e;
  }
};

const EMPTY_FORM = {
  name: "",
  category: "Art & Craft",
  blurb: "",
  price: "",
  is_free: false,
  discount_pct: 0,
  sizesText: "",
  inventory: "",
  images: [],
  focals: {},
  video: "",
  download_file: "",
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
};

export default function StoreAdmin({ currentUser }) {
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | product

  const loadProducts = () => authed("/api/store/admin/products").then(setProducts).catch((e) => toast.error(e.message));
  const loadCategories = () =>
    fetch(`${API}/api/store/categories`).then((r) => r.json()).then(setCategories).catch((e) => console.error(e));
  const loadPromos = () => authed("/api/store/admin/promos").then(setPromos).catch((e) => toast.error(e.message));

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadPromos();
  }, []);

  const TABS = [
    { key: "products", label: "Products", icon: Package },
    { key: "categories", label: "Categories", icon: Tag },
    { key: "promos", label: "Promo Codes", icon: Percent },
  ];

  return (
    <div className={`${ccCard} p-6`} data-testid="store-admin">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h3 className="text-base font-extrabold font-heading flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-full bg-[#E6F2FA] flex items-center justify-center">
            <Store className="w-4 h-4 text-[#5CB3DC]" />
          </span>
          Store Manager
          <span className="text-[10px] font-bold text-[#9CA3AF] uppercase tracking-wider">{products.filter((p) => p.active).length} live items</span>
        </h3>
        <div className="flex gap-2">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`store-admin-tab-${t.key}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold transition-all ${tab === t.key ? "bg-[#B3E0F2] text-[#374151] shadow-sm" : "text-[#9CA3AF] hover:text-[#374151]"}`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
          {tab === "products" && (
            <button onClick={() => setEditing("new")} data-testid="store-admin-add-btn" className={ccBtnPink}>
              <Plus className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Add Item
            </button>
          )}
        </div>
      </div>

      {tab === "products" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="store-admin-products">
          {products.map((p) => (
            <div
              key={p.id}
              data-testid={`store-admin-card-${p.id}`}
              className={`rounded-2xl border p-4 flex gap-3 ${p.active ? "bg-[#FFF9F2] border-[#FCE6E9] dark:bg-[#273244] dark:border-[#374457]" : "bg-gray-50 border-gray-200 opacity-60"}`}
            >
              {p.images?.[0] ? (
                <img src={mediaUrl(p.images[0])} alt={p.name} className="w-16 h-16 rounded-xl object-cover shrink-0" />
              ) : (
                <span className="w-16 h-16 rounded-xl bg-[#E6F2FA] flex items-center justify-center shrink-0">
                  <ImagePlus className="w-5 h-5 text-[#5CB3DC]" />
                </span>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-black text-[#374151] dark:text-slate-100 truncate">{p.name}</p>
                <p className="text-[10px] text-[#9CA3AF] font-bold">
                  {p.category}
                  {p.is_free ? " · FREE" : p.discount_pct ? ` · ${money(p.price)} → -${p.discount_pct}%` : ` · ${money(p.price)}`}
                  {p.inventory != null ? ` · ${p.inventory} in stock` : ""}
                  {!p.active ? " · removed" : ""}
                </p>
                <p className="text-[10px] text-[#9CA3AF] font-medium truncate">by {p.created_by_name || "NestTrack Team"}</p>
                <div className="flex gap-1.5 mt-2">
                  <button onClick={() => setEditing(p)} data-testid={`store-admin-edit-${p.id}`} className="flex items-center gap-1 px-2.5 py-1 bg-white border border-[#B3E0F2] rounded-full text-[10px] font-bold text-[#3D7E9E] hover:bg-[#E6F2FA]">
                    <Pencil className="w-3 h-3" /> Edit
                  </button>
                  {p.active ? (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Remove "${p.name}" from the store?`)) return;
                        try {
                          await authed(`/api/store/admin/products/${p.id}`, { method: "DELETE" });
                          toast.success("Item removed from the store.");
                          loadProducts();
                        } catch (e) {
                          toast.error(e.message);
                        }
                      }}
                      data-testid={`store-admin-delete-${p.id}`}
                      className="flex items-center gap-1 px-2.5 py-1 bg-white border border-[#F8C8DC] rounded-full text-[10px] font-bold text-[#B04A6B] hover:bg-[#FCE6E9]"
                    >
                      <Trash2 className="w-3 h-3" /> Remove
                    </button>
                  ) : (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Permanently delete "${p.name}"? This cannot be undone.`)) return;
                        try {
                          await authed(`/api/store/admin/products/${p.id}?permanent=true`, { method: "DELETE" });
                          toast.success("Item permanently deleted.");
                          loadProducts();
                        } catch (e) {
                          toast.error(e.message);
                        }
                      }}
                      data-testid={`store-admin-purge-${p.id}`}
                      className="flex items-center gap-1 px-2.5 py-1 bg-rose-500 border border-rose-500 rounded-full text-[10px] font-bold text-white hover:bg-rose-600"
                    >
                      <Trash2 className="w-3 h-3" /> Delete Forever
                    </button>
                  )}
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/api/share/product/${p.id}`);
                      toast.success("Share link copied — full preview card included.");
                    }}
                    data-testid={`store-admin-share-${p.id}`}
                    className="flex items-center gap-1 px-2.5 py-1 bg-white border border-[#F5E3B3] rounded-full text-[10px] font-bold text-[#A08244] hover:bg-[#FFF2D1]"
                  >
                    <Share2 className="w-3 h-3" /> Share
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "categories" && <CategoriesTab categories={categories} onChanged={loadCategories} />}

      {tab === "promos" && <PromosTab promos={promos} onChanged={loadPromos} />}

      {editing && (
        <ProductForm
          product={editing === "new" ? null : editing}
          categories={categories}
          currentUser={currentUser}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            loadProducts();
          }}
        />
      )}
    </div>
  );
}

// ---------- Product add/edit form ----------
const ProductForm = ({ product, categories, currentUser, onClose, onSaved }) => {
  const [form, setForm] = useState(() =>
    product
      ? {
          name: product.name,
          category: product.category,
          blurb: product.blurb || "",
          price: product.price || "",
          is_free: !!product.is_free,
          discount_pct: product.discount_pct || 0,
          sizesText: (product.sizes || []).join(", "),
          inventory: product.inventory != null ? String(product.inventory) : "",
          images: product.images || [],
          focals: product.focals || {},
          video: product.video || "",
          download_file: product.download_file || "",
          meta_title: product.meta_title || "",
          meta_description: product.meta_description || "",
          meta_keywords: product.meta_keywords || "",
        }
      : EMPTY_FORM
  );
  const [uploading, setUploading] = useState("");
  const [cropQueue, setCropQueue] = useState([]);
  const [focalFor, setFocalFor] = useState(null);

  const uploadCropped = async (cropped) => {
    setCropQueue((q) => q.slice(1));
    setUploading("Uploading photo…");
    try {
      const res = await uploadMedia(cropped);
      setForm((f) => ({ ...f, images: [...f.images, res.path] }));
      setFocalFor(res.path);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading("");
    }
  };
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const doUploads = async (files, apply) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    try {
      for (let i = 0; i < list.length; i++) {
        setUploading(`Uploading ${i + 1}/${list.length}…`);
        try {
          const res = await uploadMedia(list[i]);
          apply(res.path);
        } catch (e) {
          toast.error(e.message);
        }
      }
    } finally {
      setUploading(""); // always release the save button, even if an upload throws
    }
  };

  const autoMeta = () => {
    const desc = form.blurb.trim();
    setForm((f) => ({
      ...f,
      meta_title: f.name.trim(),
      meta_description: `${desc.length > 140 ? desc.slice(0, 137).trimEnd() + "..." : desc}${desc ? " — " : " — "}${f.category} on the NestTrack Store.`,
      meta_keywords: [f.name.trim(), f.category, "daycare supplies", "childcare", "NestTrack Store"].filter(Boolean).join(", "),
    }));
    toast.success("SEO tags auto-generated — tweak them below if you like.");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.is_free && !(parseFloat(form.price) > 0)) return toast.error("Set a price, or mark the item as free.");
    if (!form.images.length) return toast.error("Add at least one photo.");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        category: form.category,
        blurb: form.blurb.trim(),
        price: parseFloat(form.price) || 0,
        is_free: form.is_free,
        discount_pct: parseInt(form.discount_pct, 10) || 0,
        sizes: form.sizesText.split(",").map((s) => s.trim()).filter(Boolean),
        inventory: form.inventory === "" ? null : Math.max(0, parseInt(form.inventory, 10) || 0),
        images: form.images,
        focals: form.focals || {},
        video: form.video,
        download_file: form.download_file,
        meta_title: form.meta_title,
        meta_description: form.meta_description,
        meta_keywords: form.meta_keywords,
      };
      if (product) await authed(`/api/store/admin/products/${product.id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      else await authed("/api/store/admin/products", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      toast.success(product ? "Item updated." : "Item added to the store.");
      onSaved();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const thumb = currentUser?.avatar;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#374151]/40 backdrop-blur-sm" onClick={onClose} data-testid="store-product-form">
      <form
        onSubmit={save}
        className="bg-white rounded-3xl border border-[#FCE6E9] shadow-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto p-6 space-y-4 dark:bg-[#273244] dark:border-[#374457]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-black font-heading text-[#374151] dark:text-slate-100">{product ? "Edit Item" : "Add Store Item"}</h3>
          <div className="flex items-center gap-2">
            {thumb ? (
              <img src={thumb} alt="" className="w-7 h-7 rounded-full object-cover border border-[#F8C8DC]" />
            ) : (
              <span className="w-7 h-7 rounded-full bg-[#FCE6E9] flex items-center justify-center text-[10px] font-black text-[#E5A0BC]">{(currentUser?.fullName || "N").charAt(0)}</span>
            )}
            <span className="text-[10px] font-bold text-[#9CA3AF]" data-testid="sprod-listed-by">Listing as {currentUser?.fullName}</span>
            <button type="button" onClick={onClose} data-testid="sprod-close" className="w-8 h-8 rounded-full bg-[#FFF9F2] flex items-center justify-center text-[#9CA3AF] hover:text-[#374151]">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2 sm:col-span-1">
            <label className={ccLabel}>Item Name</label>
            <input required type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className={ccInput} data-testid="sprod-name" />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className={ccLabel}>Category / Section</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} className={ccInput} data-testid="sprod-category">
              {categories.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={ccLabel}>Description</label>
          <textarea rows={2} value={form.blurb} onChange={(e) => set("blurb", e.target.value)} className={ccInput} data-testid="sprod-blurb" />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <label className={ccLabel}>Price (USD)</label>
            <input type="number" min="0" step="0.01" value={form.price} onChange={(e) => set("price", e.target.value)} disabled={form.is_free} className={ccInput} data-testid="sprod-price" placeholder="19.99" />
          </div>
          <div>
            <label className={ccLabel}>Discount %</label>
            <input type="number" min="0" max="95" value={form.discount_pct} onChange={(e) => set("discount_pct", e.target.value)} disabled={form.is_free} className={ccInput} data-testid="sprod-discount" />
          </div>
          <div>
            <label className={ccLabel}>Inventory (blank = unlimited)</label>
            <input type="number" min="0" value={form.inventory} onChange={(e) => set("inventory", e.target.value)} className={ccInput} data-testid="sprod-inventory" placeholder="∞" />
          </div>
          <div>
            <label className={ccLabel}>Sizes (comma separated)</label>
            <input type="text" value={form.sizesText} onChange={(e) => set("sizesText", e.target.value)} className={ccInput} data-testid="sprod-sizes" placeholder="S, M, L" />
          </div>
        </div>

        <label className="flex items-center gap-2.5 bg-[#E2F4E1]/70 border border-[#C4E5C2] rounded-2xl px-4 py-3 cursor-pointer" data-testid="sprod-free-label">
          <input type="checkbox" checked={form.is_free} onChange={(e) => set("is_free", e.target.checked)} data-testid="sprod-free-toggle" className="w-4 h-4 accent-[#7CC98F]" />
          <span className="text-xs font-bold text-[#2F6031]">Free item — e.g. a downloadable book or resource. No charge at checkout.</span>
        </label>

        {/* Photos */}
        <div>
          <label className={ccLabel}>Photos (multiple)</label>
          <div className="flex gap-2 flex-wrap mb-2">
            {form.images.map((img) => (
              <div key={img} className="relative">
                <img src={mediaUrl(img)} alt="" className="w-16 h-16 rounded-xl object-cover border border-[#FCE6E9]" style={(form.focals || {})[img] ? { objectPosition: `${form.focals[img].x}% ${form.focals[img].y}%` } : undefined} />
                <button type="button" onClick={() => set("images", form.images.filter((x) => x !== img))} data-testid={`sprod-photo-remove-${form.images.indexOf(img)}`} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-[#B04A6B] text-white rounded-full text-[10px] flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
                <button type="button" onClick={() => setFocalFor(img)} title="Adjust focus point" data-testid={`sprod-photo-focus-${form.images.indexOf(img)}`} className="absolute -bottom-1.5 -left-1.5 w-5 h-5 bg-white text-[#3D7E9E] border border-[#B3E0F2] rounded-full text-[10px] flex items-center justify-center">
                  <Crosshair className="w-3 h-3" />
                </button>
              </div>
            ))}
            <label className="w-16 h-16 rounded-xl border-2 border-dashed border-[#F8C8DC] flex items-center justify-center cursor-pointer hover:bg-[#FCE6E9]/40" data-testid="sprod-photo-add">
              <ImagePlus className="w-5 h-5 text-[#E5A0BC]" />
              <input type="file" accept="image/*" multiple className="hidden" data-testid="sprod-photo-input" onChange={(e) => { const l = Array.from(e.target.files || []); e.target.value = ""; if (l.length) setCropQueue(l); }} />
            </label>
          </div>
        </div>

        {/* Video + download file */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={ccLabel}>Video (optional)</label>
            {form.video ? (
              <div className="flex items-center gap-2 bg-[#E6F2FA]/60 border border-[#B3E0F2] rounded-2xl px-3 py-2.5">
                <Film className="w-4 h-4 text-[#3D7E9E] shrink-0" />
                <span className="text-[11px] font-bold text-[#3D7E9E] truncate flex-1">Video attached</span>
                <button type="button" onClick={() => set("video", "")} data-testid="sprod-video-remove" className="text-[#B04A6B]"><X className="w-4 h-4" /></button>
              </div>
            ) : (
              <label className="flex items-center gap-2 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl px-3 py-2.5 cursor-pointer hover:bg-[#FCE6E9]/40" data-testid="sprod-video-add">
                <Film className="w-4 h-4 text-[#9CA3AF]" />
                <span className="text-[11px] font-bold text-[#9CA3AF]">Upload video</span>
                <input type="file" accept="video/*" className="hidden" data-testid="sprod-video-input" onChange={(e) => doUploads(e.target.files, (path) => set("video", path))} />
              </label>
            )}
          </div>
          <div>
            <label className={ccLabel}>Digital File (PDF / ebook / ZIP — optional, buyers download after purchase)</label>
            {form.download_file ? (
                <div className="flex items-center gap-2 bg-[#E2F4E1]/70 border border-[#C4E5C2] rounded-2xl px-3 py-2.5">
                  <FileDown className="w-4 h-4 text-[#2F6031] shrink-0" />
                  <span className="text-[11px] font-bold text-[#2F6031] truncate flex-1">File attached</span>
                  <button type="button" onClick={() => set("download_file", "")} data-testid="sprod-download-remove" className="text-[#B04A6B]"><X className="w-4 h-4" /></button>
                </div>
              ) : (
                <label className="flex items-center gap-2 bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl px-3 py-2.5 cursor-pointer hover:bg-[#FCE6E9]/40" data-testid="sprod-download-add">
                  <FileDown className="w-4 h-4 text-[#9CA3AF]" />
                  <span className="text-[11px] font-bold text-[#9CA3AF]">Upload file</span>
                  <input type="file" accept=".pdf,.epub,.zip" className="hidden" data-testid="sprod-download-input" onChange={(e) => doUploads(e.target.files, (path) => set("download_file", path))} />
                </label>
              )}
          </div>
        </div>

        {/* SEO */}
        <div className="bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl p-4 space-y-3 dark:bg-[#232D3D] dark:border-[#374457]">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-extrabold uppercase tracking-wider text-[#9CA3AF]">SEO &amp; Share Meta</p>
            <button type="button" onClick={autoMeta} data-testid="sprod-meta-auto-btn" className="flex items-center gap-1 px-3 py-1.5 bg-[#F8C8DC] rounded-full text-[10px] font-bold text-[#374151] hover:bg-[#f3b4cd]">
              <Sparkles className="w-3 h-3" /> Auto-Generate
            </button>
          </div>
          <input type="text" value={form.meta_title} onChange={(e) => set("meta_title", e.target.value)} placeholder="Meta title (auto from name if blank)" className={ccInput} data-testid="sprod-meta-title" />
          <textarea rows={2} value={form.meta_description} onChange={(e) => set("meta_description", e.target.value)} placeholder="Meta description (auto if blank)" className={ccInput} data-testid="sprod-meta-description" />
          <input type="text" value={form.meta_keywords} onChange={(e) => set("meta_keywords", e.target.value)} placeholder="Keywords, comma separated (auto if blank)" className={ccInput} data-testid="sprod-meta-keywords" />
        </div>

        {uploading && <p className="text-xs font-bold text-[#3D7E9E]" data-testid="sprod-uploading">{uploading}</p>}
        {cropQueue.length > 0 && <ImageCropModal file={cropQueue[0]} defaultAspect={1} onCancel={() => setCropQueue([])} onDone={uploadCropped} />}
        {focalFor && (
          <FocalPointModal
            src={mediaUrl(focalFor)}
            initial={(form.focals || {})[focalFor] || { x: 50, y: 50 }}
            onCancel={() => setFocalFor(null)}
            onDone={(pt) => {
              setForm((f) => ({ ...f, focals: { ...(f.focals || {}), [focalFor]: pt } }));
              setFocalFor(null);
              toast.success("Focus point saved.");
            }}
          />
        )}

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className={ccBtnGhost} data-testid="sprod-cancel">Cancel</button>
          <button type="submit" disabled={saving || !!uploading} className={`${ccBtnPink} disabled:opacity-50`} data-testid="sprod-submit">
            {saving ? "Saving…" : product ? "Save Changes" : "Add to Store"}
          </button>
        </div>
      </form>
    </div>
  );
};

// ---------- Categories ----------
const CategoriesTab = ({ categories, onChanged }) => {
  const [name, setName] = useState("");
  const add = async (e) => {
    e.preventDefault();
    try {
      await authed("/api/store/admin/categories", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }) });
      toast.success(`Section "${name.trim()}" created.`);
      setName("");
      onChanged();
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <div data-testid="store-admin-categories">
      <form onSubmit={add} className="flex gap-2 mb-4 max-w-md">
        <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="New section name — e.g. Outdoor Play" className={ccInput} data-testid="scat-input" />
        <button type="submit" className={ccBtnPink} data-testid="scat-add-btn"><Plus className="w-4 h-4" /></button>
      </form>
      <div className="flex gap-2 flex-wrap">
        {categories.map((c) => (
          <span key={c} className="flex items-center gap-1.5 px-3.5 py-2 bg-[#E6F2FA] border border-[#B3E0F2] rounded-full text-xs font-bold text-[#3D7E9E]" data-testid={`scat-pill-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            {c}
            <button
              onClick={async () => {
                try {
                  await authed(`/api/store/admin/categories/${encodeURIComponent(c)}`, { method: "DELETE" });
                  toast.success(`Section "${c}" removed.`);
                  onChanged();
                } catch (err) {
                  toast.error(err.message);
                }
              }}
              data-testid={`scat-delete-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="text-[#B04A6B] hover:text-[#8d3553]"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </span>
        ))}
      </div>
      <p className="text-[11px] text-[#9CA3AF] font-medium mt-3">Removing a section doesn't delete its items — they keep their label until edited.</p>
    </div>
  );
};

// ---------- Promo codes ----------
const PromosTab = ({ promos, onChanged }) => {
  const [code, setCode] = useState("");
  const [pct, setPct] = useState("10");
  const add = async (e) => {
    e.preventDefault();
    try {
      await authed("/api/store/admin/promos", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ code, percent_off: parseInt(pct, 10) }) });
      toast.success(`Promo ${code.trim().toUpperCase()} is live — customers can enter it at checkout.`);
      setCode("");
      onChanged();
    } catch (err) {
      toast.error(err.message);
    }
  };
  return (
    <div data-testid="store-admin-promos">
      <form onSubmit={add} className="flex gap-2 mb-4 max-w-lg flex-wrap">
        <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CODE — e.g. NEST10" className={`${ccInput} max-w-[220px]`} data-testid="spromo-code" />
        <input type="number" min="1" max="100" value={pct} onChange={(e) => setPct(e.target.value)} className={`${ccInput} max-w-[110px]`} data-testid="spromo-pct" />
        <button type="submit" className={ccBtnPink} data-testid="spromo-add-btn">Create Promo</button>
      </form>
      {promos.length === 0 ? (
        <p className="text-sm font-bold text-[#9CA3AF]" data-testid="spromo-empty">No active promo codes yet.</p>
      ) : (
        <div className="space-y-2">
          {promos.map((p) => (
            <div key={p.id} className="flex items-center justify-between bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl px-4 py-3 dark:bg-[#273244] dark:border-[#374457]" data-testid={`spromo-row-${p.code}`}>
              <div>
                <p className="text-sm font-black text-[#374151] dark:text-slate-100">{p.code}</p>
                <p className="text-[11px] text-[#9CA3AF] font-bold">{p.percent_off}% off · one-time use per customer · entered at Stripe checkout</p>
              </div>
              <button
                onClick={async () => {
                  try {
                    await authed(`/api/store/admin/promos/${p.id}`, { method: "DELETE" });
                    toast.success(`Promo ${p.code} retired.`);
                    onChanged();
                  } catch (err) {
                    toast.error(err.message);
                  }
                }}
                data-testid={`spromo-delete-${p.code}`}
                className="flex items-center gap-1 px-3 py-1.5 bg-white border border-[#F8C8DC] rounded-full text-[10px] font-bold text-[#B04A6B] hover:bg-[#FCE6E9]"
              >
                <Trash2 className="w-3 h-3" /> Retire
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
